/**
 * Passenger types in a flight search, and how they travel in the results
 * page's URL.
 *
 * A seated infant (under two, with a seat of their own) is `infant_in_seat`:
 * the type every pricing call counts for Google's `infants_in_seat`, and that
 * Duffel searches as a child. The passenger picker used to call it
 * `infant_with_seat`, which no pricing call counted and the results URL
 * dropped, so a baby picked "on seat" on the home page vanished from the
 * search, the price and checkout. isSeatedInfant still reads that old name as
 * the same traveler, for anything saved before the change.
 *
 * Every search form writes the results URL and the results page reads it
 * back; both go through here so the two can't drift apart. (Ops tickets each
 * traveler by date of birth, so these types only decide what is searched and
 * priced, not how a ticket is issued.)
 */
export const SEATED_INFANT = 'infant_in_seat';
export const LAP_INFANT = 'infant_without_seat';
/** Google Flights' party-size limit; also caps a hand-edited URL. */
const MAX_PER_TYPE = 9;

export const isSeatedInfant = (type) => type === SEATED_INFANT || type === 'infant_with_seat';

const count = (passengers, test) => (
  Array.isArray(passengers) ? passengers.filter((p) => p && test(p.type)).length : 0
);

/** Passenger counts by type. */
export function passengerCounts(passengers) {
  return {
    adults: count(passengers, (type) => type === 'adult'),
    children: count(passengers, (type) => type === 'child'),
    seatedInfants: count(passengers, isSeatedInfant),
    lapInfants: count(passengers, (type) => type === LAP_INFANT),
  };
}

/**
 * The party as Google Flights (SerpAPI) prices it: `adults` (at least one),
 * then `children`, `infants_in_seat` and `infants_on_lap` when there are any.
 * Every call that prices a party sends all four. Lap infants were never sent
 * (2026-10-28): a family's fare left the baby out (JFK-LHR on BA: $300 for
 * the adult alone, $342 with a lap infant), and the admin price checks priced
 * a lap infant as a seated one.
 */
export function googleFlightsPartyParams(passengers) {
  const counts = passengerCounts(passengers);
  const adults = Math.max(1, counts.adults);
  // One lap per adult (airline rule, and Google refuses more): any other
  // infant needs a seat of its own, so it is priced as a seated infant.
  const lapInfants = Math.min(counts.lapInfants, adults);
  const seatedInfants = counts.seatedInfants + (counts.lapInfants - lapInfants);
  const params = { adults: String(adults) };
  if (counts.children > 0) params.children = String(counts.children);
  if (seatedInfants > 0) params.infants_in_seat = String(seatedInfants);
  if (lapInfants > 0) params.infants_on_lap = String(lapInfants);
  return params;
}

/** Write `passengers` into a results-page query (URLSearchParams); returns it. */
export function writePassengerParams(query, passengers) {
  const { adults, children, seatedInfants, lapInfants } = passengerCounts(passengers);
  query.set('adults', String(adults));
  if (children > 0) query.set('children', String(children));
  if (seatedInfants > 0) query.set('infantsInSeat', String(seatedInfants));
  if (lapInfants > 0) query.set('infants', String(lapInfants));
  return query;
}

/**
 * The passengers a results-page query holds, in the order the results page
 * searches them: adults (at least one), children, seated infants, then lap
 * infants. `infants` is lap infants, as it always was.
 */
export function readPassengerParams(query) {
  const read = (key, min) => Math.min(MAX_PER_TYPE, Math.max(min, parseInt(query.get(key), 10) || 0));
  const several = (n, type) => Array.from({ length: n }, () => ({ type }));
  return [
    ...several(read('adults', 1), 'adult'),
    ...several(read('children', 0), 'child'),
    ...several(read('infantsInSeat', 0), SEATED_INFANT),
    ...several(read('infants', 0), LAP_INFANT),
  ];
}
